import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiFetch } from '../api/client';
import type {
  CurrentValue,
  KeyChange,
  KeyChangeType,
  RecentGroupMeta,
  RecentKeysResponse,
} from '../api/types';
import { LocaleSelector } from '../components/LocaleSelector';
import { rowDomId } from '../components/rowId';
import { useVisibleLocales } from '../hooks/useVisibleLocales';

const DAYS_OPTIONS = [7, 14, 30] as const;

const TYPE_LABEL: Record<KeyChangeType, string> = {
  added: '追加',
  modified: '更新',
  removed: '削除',
};

// セルの枠線色。種別ごとに変化量がぱっと見で分かるように分ける。
const TYPE_CELL_STYLE: Record<KeyChangeType, string> = {
  added: 'border-green-300 bg-green-50',
  modified: 'border-amber-300 bg-amber-50',
  removed: 'border-red-300 bg-red-50',
};

const TYPE_BADGE_STYLE: Record<KeyChangeType, string> = {
  added: 'bg-green-200 text-green-900',
  modified: 'bg-amber-200 text-amber-900',
  removed: 'bg-red-200 text-red-900',
};

const truncate = (value: string | null, max: number): string => {
  if (value === null) return '';
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
};

// (groupId, locale, key) → 現在値 の lookup。「変更なし」セルで現在値を薄く出すために使う。
type CurrentValueLookup = Map<string, Map<string, Map<string, string>>>;

const buildCurrentValueLookup = (values: CurrentValue[]): CurrentValueLookup => {
  const root: CurrentValueLookup = new Map();
  for (const v of values) {
    const byLocale = root.get(v.groupId) ?? new Map<string, Map<string, string>>();
    const byKey = byLocale.get(v.locale) ?? new Map<string, string>();
    byKey.set(v.key, v.value);
    byLocale.set(v.locale, byKey);
    root.set(v.groupId, byLocale);
  }
  return root;
};

// 各 group ごとに「key → locale → KeyChange」に畳んだ表データ。
type GroupTable = {
  group: RecentGroupMeta;
  keys: string[];
  /** key → locale → 変更内容 */
  cells: Map<string, Map<string, KeyChange>>;
  /** locale → その locale で変更があった件数 */
  changedCountByLocale: Map<string, number>;
};

const buildGroupTables = (groups: RecentGroupMeta[], changes: KeyChange[]): GroupTable[] => {
  const byGroup = new Map<string, KeyChange[]>();
  for (const c of changes) {
    const list = byGroup.get(c.groupId);
    if (list === undefined) byGroup.set(c.groupId, [c]);
    else list.push(c);
  }

  const tables: GroupTable[] = [];
  for (const group of groups) {
    const list = byGroup.get(group.id);
    if (list === undefined || list.length === 0) continue;

    const cells = new Map<string, Map<string, KeyChange>>();
    const changedCountByLocale = new Map<string, number>();
    const keySet = new Set<string>();
    for (const c of list) {
      keySet.add(c.key);
      const row = cells.get(c.key) ?? new Map<string, KeyChange>();
      row.set(c.locale, c);
      cells.set(c.key, row);
      changedCountByLocale.set(c.locale, (changedCountByLocale.get(c.locale) ?? 0) + 1);
    }
    const keys = Array.from(keySet).sort();
    tables.push({ group, keys, cells, changedCountByLocale });
  }
  tables.sort((a, b) => a.group.name.localeCompare(b.group.name));
  return tables;
};

type CellProps = {
  change: KeyChange | undefined;
  currentValue: string | undefined;
};

const ChangeCell = ({ change, currentValue }: CellProps) => {
  if (change === undefined) {
    // 変更なし。head に値があれば薄く表示、無ければ `—`。
    if (currentValue === undefined) {
      return <span className="text-gray-300">—</span>;
    }
    return <div className="text-[12px] text-gray-500">{truncate(currentValue, 80)}</div>;
  }
  return (
    <div className={`space-y-0.5 rounded border px-2 py-1 ${TYPE_CELL_STYLE[change.type]}`}>
      <div className="flex items-center gap-1">
        <span
          className={`inline-block rounded px-1 py-0.5 text-[9px] font-semibold ${TYPE_BADGE_STYLE[change.type]}`}
        >
          {TYPE_LABEL[change.type]}
        </span>
      </div>
      {change.type === 'modified' && (
        <>
          <div className="text-[11px] text-red-700 line-through">
            {truncate(change.oldValue, 80)}
          </div>
          <div className="text-[12px] text-green-800">{truncate(change.newValue, 80)}</div>
        </>
      )}
      {change.type === 'added' && (
        <div className="text-[12px] text-green-800">{truncate(change.newValue, 80)}</div>
      )}
      {change.type === 'removed' && (
        <div className="text-[12px] text-red-700 line-through">{truncate(change.oldValue, 80)}</div>
      )}
    </div>
  );
};

type GroupTableSectionProps = {
  table: GroupTable;
  visible: Set<string>;
  onToggle: (locale: string) => void;
  currentValues: CurrentValueLookup;
};

const GroupTableSection = ({ table, visible, onToggle, currentValues }: GroupTableSectionProps) => {
  const { group, keys, cells, changedCountByLocale } = table;
  // 表示 locale: baseLocale + visible に入ってる locale (group が持ってる locale のみ)
  const visibleLocales = group.locales.filter((l) => l === group.baseLocale || visible.has(l));

  const groupCurrent = currentValues.get(group.id);

  return (
    <section className="rounded border bg-white">
      <header className="flex items-center justify-between border-b bg-gray-50 px-4 py-2">
        <h3 className="text-sm font-medium">
          <Link to={`/groups/${group.id}`} className="hover:underline">
            {group.name}
          </Link>
        </h3>
        <span className="text-xs text-gray-500">{keys.length} key</span>
      </header>
      <div className="px-4 py-2">
        <LocaleSelector
          baseLocale={group.baseLocale}
          allLocales={group.locales}
          visible={visible}
          onToggle={onToggle}
          changedCountByLocale={changedCountByLocale}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full table-fixed border-collapse text-sm">
          <thead className="border-b bg-white text-xs text-gray-600">
            <tr>
              <th className="w-72 px-3 py-2 text-left">key</th>
              {visibleLocales.map((locale) => (
                <th key={locale} className="px-3 py-2 text-left">
                  <span>
                    {locale}
                    {locale === group.baseLocale && (
                      <span className="ml-1 rounded bg-gray-200 px-1 text-[10px] text-gray-700">
                        base
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => {
              const row = cells.get(key);
              return (
                <tr key={key} className="border-b align-top">
                  <td className="px-3 py-2 font-mono text-xs break-all">
                    <Link
                      to={`/groups/${group.id}#${rowDomId(key)}`}
                      className="text-blue-700 hover:underline"
                      title="編集画面で開く"
                    >
                      {key}
                    </Link>
                  </td>
                  {visibleLocales.map((locale) => (
                    <td key={locale} className="px-2 py-2 text-xs">
                      <ChangeCell
                        change={row?.get(locale)}
                        currentValue={groupCurrent?.get(locale)?.get(key)}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const Recent = () => {
  const [days, setDays] = useState<number>(7);
  const { visible, toggle } = useVisibleLocales();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['recent-keys', days],
    queryFn: () => apiFetch<RecentKeysResponse>(`/api/recent-keys?days=${String(days)}`),
    // 重い処理なので頻繁な refetch は避け手動更新中心にする。
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const tables = useMemo(
    () => (data === undefined ? [] : buildGroupTables(data.groups, data.changes)),
    [data],
  );

  const currentValues = useMemo(
    () => (data === undefined ? new Map() : buildCurrentValueLookup(data.currentValues)),
    [data],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">最近更新されたキー</h2>
          <p className="text-xs text-gray-500">
            main ブランチで過去 {days} 日に追加 / 更新 / 削除された翻訳キーの一覧
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">
            期間:
            <select
              value={days}
              onChange={(e) => setDays(Number.parseInt(e.target.value, 10))}
              className="ml-1.5 rounded border px-2 py-1 text-xs"
            >
              {DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} 日
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isFetching ? '更新中...' : '再取得'}
          </button>
        </div>
      </div>

      {isLoading && <div className="text-sm text-gray-500">読み込み中...</div>}
      {error !== null && <div className="text-sm text-red-700">{error.message}</div>}

      {data !== undefined && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            {data.changes.length} 件のキー変更 ({tables.length} グループ) / {data.commitCount}{' '}
            コミット
          </p>

          {data.changes.length === 0 && (
            <div className="rounded border bg-white px-4 py-8 text-center text-sm text-gray-500">
              過去 {days} 日に翻訳ファイルの変更はありません
            </div>
          )}

          {tables.map((table) => (
            <GroupTableSection
              key={table.group.id}
              table={table}
              visible={visible}
              onToggle={toggle}
              currentValues={currentValues}
            />
          ))}
        </div>
      )}
    </div>
  );
};
